// components/schedule/ScheduleDialog/useScheduleForm.ts

import { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { Schedule } from '../../../types/schedule';
import { OtherSchedule } from '../../../types/otherSchedule';
import { CombinedScheduleItem } from '../../../types/commonSchedule';
import { SelectChangeEvent } from '@mui/material';

export const useScheduleForm = (
    initialData: CombinedScheduleItem | null,
    selectedDate: Date | null,
    initialTeam: 'equipment' | 'scaffolding' | null,
    initialScheduleType: 'construction' | 'other' = 'construction'
) => {
    // scheduleTypeの初期値設定
    const [scheduleType, setScheduleType] = useState<'construction' | 'other'>(
        initialData?.type || initialScheduleType
    );

    // 重要：initialDataとinitialScheduleTypeが変わった時にscheduleTypeを更新
    useEffect(() => {
        if (initialData) {
            setScheduleType(initialData.type);
        } else {
            setScheduleType(initialScheduleType);
        }
    }, [initialData, initialScheduleType]);

    // 工事予定用フォームデータ
    const [constructionFormData, setConstructionFormData] = useState<Schedule>({
        constructionId: 0,
        scheduleDate: '',
        isAllDay: false,
        meetingTime: '07:00', // デフォルトの集合時間を追加
        teamType: initialTeam || 'equipment',
        memberData: [],
        vehicleData: [],
        requiredWorkers: 0
    });

    // その他予定用フォームデータ
    const [otherFormData, setOtherFormData] = useState<OtherSchedule>({
        scheduleDate: '',
        isAllDay: false,
        meetingTime: '07:00', // デフォルトの集合時間を追加
        teamType: initialTeam || 'equipment',
        memberData: [],
        vehicleData: [],
        requiredWorkers: 0,
        scheduleContent: ''
    });

    // 初期データがある場合（編集モード）または新規作成時の初期設定
    useEffect(() => {
        
        // 日付を適切に処理
        let dateToUse = '';

        if (initialData?.scheduleDate) {
            // 日付文字列をパースして日本時間に調整
            const dateObj = new Date(initialData.scheduleDate);
            // UTCとの差を補正（+1日）
            dateObj.setDate(dateObj.getDate());
            dateToUse = format(dateObj, 'yyyy-MM-dd');
        } else if (selectedDate) {
            dateToUse = format(selectedDate, 'yyyy-MM-dd');
        } else {
            dateToUse = format(new Date(), 'yyyy-MM-dd');
        }

        // 予定タイプに応じて適切なフォームデータを設定
        if (initialData) {
            if (initialData.type === 'construction') {
                setScheduleType('construction');
                setConstructionFormData({
                    id: initialData.id,
                    constructionId: initialData.constructionId || 0,
                    siteName: initialData.siteName,
                    customerName: initialData.customerName,
                    scheduleDate: dateToUse,
                    startTime: initialData.startTime,
                    endTime: initialData.endTime,
                    meetingTime: initialData.meetingTime || '07:00', // 集合時間を追加
                    isAllDay: initialData.isAllDay,
                    notes: initialData.notes,
                    teamType: initialData.teamType || initialTeam || 'equipment',
                    memberData: initialData.memberData || [],
                    vehicleData: initialData.vehicleData || [],
                    requiredWorkers: initialData.requiredWorkers || 0
                });
            } else {
                setScheduleType('other');
                setOtherFormData({
                    id: initialData.id,
                    customerId: initialData.customerId,
                    customerName: initialData.customerName,
                    scheduleDate: dateToUse,
                    startTime: initialData.startTime,
                    endTime: initialData.endTime,
                    meetingTime: initialData.meetingTime || '07:00', // 集合時間を追加
                    isAllDay: initialData.isAllDay,
                    scheduleContent: initialData.scheduleContent,
                    notes: initialData.notes,
                    teamType: initialData.teamType || initialTeam || 'equipment',
                    memberData: (initialData.memberData as any) || [],
                    vehicleData: initialData.vehicleData || [],
                    requiredWorkers: initialData.requiredWorkers || 0
                });
            }
        } else {
            // 新規作成時のデフォルト値
            // 工事予定のデフォルト
            setConstructionFormData({
                constructionId: 0,
                scheduleDate: dateToUse,
                isAllDay: false,
                meetingTime: '07:00', // デフォルトの集合時間を追加
                teamType: initialTeam || 'equipment',
                memberData: [],
                vehicleData: [],
                requiredWorkers: 0
            });

            // その他予定のデフォルト
            setOtherFormData({
                scheduleDate: dateToUse,
                isAllDay: false,
                meetingTime: '07:00', // デフォルトの集合時間を追加
                teamType: initialTeam || 'equipment',
                scheduleContent: '',
                memberData: [],
                vehicleData: [],
                requiredWorkers: 0
            });
        }
    }, [initialData, selectedDate, initialTeam, initialScheduleType]);

    // 予定タイプ切り替え
    const handleScheduleTypeChange = (type: 'construction' | 'other') => {
        setScheduleType(type);
    };

    // 工事予定フォームの入力変更ハンドラ
    const handleConstructionChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setConstructionFormData(prev => ({ ...prev, [name]: value }));
    };

    // その他予定フォームの入力変更ハンドラ
    const handleOtherChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setOtherFormData(prev => ({ ...prev, [name]: value }));
    };

    // ラジオボタンの変更ハンドラ - 工事予定
    const handleConstructionRadioChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setConstructionFormData(prev => ({ ...prev, [name]: value }));
    };

    // ラジオボタンの変更ハンドラ - その他予定
    const handleOtherRadioChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setOtherFormData(prev => ({ ...prev, [name]: value }));
    };

    // 時間タイプの変更ハンドラ - 工事予定
    const handleConstructionTimeTypeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        let updatedData = { ...constructionFormData };

        if (value === 'allDay') {
            updatedData.isAllDay = true;
            updatedData.startTime = undefined;
            updatedData.endTime = undefined;
        } else if (value === 'morning') {
            updatedData.isAllDay = false;
            updatedData.startTime = '09:00';
            updatedData.endTime = '12:00';
        } else if (value === 'afternoon') {
            updatedData.isAllDay = false;
            updatedData.startTime = '13:00';
            updatedData.endTime = '17:00';
        } else {
            updatedData.isAllDay = false;
        }

        setConstructionFormData(updatedData);
    };

    // 時間タイプの変更ハンドラ - その他予定
    const handleOtherTimeTypeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        let updatedData = { ...otherFormData };

        if (value === 'allDay') {
            updatedData.isAllDay = true;
            updatedData.startTime = undefined;
            updatedData.endTime = undefined;
        } else if (value === 'morning') {
            updatedData.isAllDay = false;
            updatedData.startTime = '09:00';
            updatedData.endTime = '12:00';
        } else if (value === 'afternoon') {
            updatedData.isAllDay = false;
            updatedData.startTime = '13:00';
            updatedData.endTime = '17:00';
        } else {
            updatedData.isAllDay = false;
        }

        setOtherFormData(updatedData);
    };

    // 工事選択の変更ハンドラ
    const handleConstructionSelectChange = (e: SelectChangeEvent) => {
        const constructionId = Number(e.target.value);
        setConstructionFormData(prev => ({ ...prev, constructionId }));
    };

    // 顧客選択の変更ハンドラ
    const handleCustomerSelectChange = (e: SelectChangeEvent) => {
        const customerId = Number(e.target.value);
        setOtherFormData(prev => ({ ...prev, customerId }));
    };

    // カスタム顧客選択ハンドラ（顧客名も保存）
    const handleCustomerSelect = (customerId: number, customerName: string) => {
        setOtherFormData(prev => ({
            ...prev,
            customerId,
            customerName
        }));
    };

    // メンバーデータ更新ハンドラ - 工事予定
    const handleConstructionMembersChange = (members: any[]) => {
        setConstructionFormData(prev => ({ ...prev, memberData: members }));
    };

    // メンバーデータ更新ハンドラ - その他予定
    const handleOtherMembersChange = (members: any[]) => {
        setOtherFormData(prev => ({ ...prev, memberData: members }));
    };

    // 車両データ更新ハンドラ - 工事予定
    const handleConstructionVehiclesChange = (vehicles: any[]) => {
        setConstructionFormData(prev => ({ ...prev, vehicleData: vehicles }));
    };

    // 車両データ更新ハンドラ - その他予定
    const handleOtherVehiclesChange = (vehicles: any[]) => {
        setOtherFormData(prev => ({ ...prev, vehicleData: vehicles }));
    };

    // クイック選択（その他予定用）
    const handleQuickContentSelect = (content: string) => {
        setOtherFormData(prev => ({ ...prev, scheduleContent: content }));
    };

    // フォームデータの検証（工事予定）
    const validateConstructionForm = (): boolean => {
        if (!constructionFormData.constructionId || !constructionFormData.scheduleDate) {
            alert('工事と日付は必須項目です');
            return false;
        }
        if (!constructionFormData.teamType) {
            alert('チーム種別（設備班/仮設班）を選択してください');
            return false;
        }
        return true;
    };

    // フォームデータの検証（その他予定）
    const validateOtherForm = (): boolean => {
        if (!otherFormData.scheduleDate) {
            alert('日付は必須項目です');
            return false;
        }
        if (!otherFormData.scheduleContent) {
            alert('予定内容は必須項目です');
            return false;
        }
        if (!otherFormData.teamType) {
            alert('チーム種別（設備班/仮設班）を選択してください');
            return false;
        }
        return true;
    };

    return {
        scheduleType,
        constructionFormData,
        otherFormData,
        handleScheduleTypeChange,
        handleConstructionChange,
        handleOtherChange,
        handleConstructionRadioChange,
        handleOtherRadioChange,
        handleConstructionTimeTypeChange,
        handleOtherTimeTypeChange,
        handleConstructionSelectChange,
        handleCustomerSelectChange,
        handleCustomerSelect,
        handleConstructionMembersChange,
        handleOtherMembersChange,
        handleConstructionVehiclesChange,
        handleOtherVehiclesChange,
        handleQuickContentSelect,
        validateConstructionForm,
        validateOtherForm
    };
};